import { useMemo } from 'react';

import { Platform, useGetPages } from '@/features/pages';
import { Departments } from '@/types';

export type ContentPageOption = {
  pageId: string;
  platform: Platform;
  name: string;
  label: string;
};

export const useContentPages = (department: Departments) => {
  const { data: instData, isLoading: isInstLoading } = useGetPages({
    platform: 'instagram',
    department,
  });
  const { data: tgData, isLoading: isTgLoading } = useGetPages({
    platform: 'telegram',
    department,
  });

  const pages = useMemo<ContentPageOption[]>(() => {
    const toName = (page: { name?: string | null; username?: string; _id: string }) =>
      page.name || page.username || page._id;

    const inst =
      instData?.pages
        .filter((page) => !page.config.isArchived)
        .map((page) => {
          const name = toName(page);
          return {
            pageId: page._id,
            platform: 'instagram' as const,
            name,
            label: `[IG] ${name}`,
          };
        }) ?? [];

    const tg =
      tgData?.pages
        .filter((page) => !page.config.isArchived)
        .map((page) => {
          const name = toName(page);
          return {
            pageId: page._id,
            platform: 'telegram' as const,
            name,
            label: `[TG] ${name}`,
          };
        }) ?? [];

    return [...inst, ...tg].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [instData?.pages, tgData?.pages]);

  const pagesByPlatform = useMemo(
    () => ({
      instagram: pages.filter((page) => page.platform === 'instagram'),
      telegram: pages.filter((page) => page.platform === 'telegram'),
    }),
    [pages]
  );

  return {
    pages,
    pagesByPlatform,
    isLoading: isInstLoading || isTgLoading,
  };
};
